import { get } from '@shared/adapters/lodash'
import { maskAccountNumber } from '@shared/utils'
import type { BankAccountWireDto, BankAccountDetailWireDto } from './bankAccountContracts'
import type { BankAccount, BankAccountDetail, CreatedBankAccount } from '../types/bankAccountTypes'

export type BankAccountDisplayStatus = 'verified' | 'pending' | 'rejected'

export type BankAccountListItemView = BankAccount & {
  maskedNumber: string
  status: BankAccountDisplayStatus
}

// Collapses the raw backend account_status enum into the three display states
// the UI renders. 'verified' is locked/approved; 'rejected' and
// 'verification_failed' both mean the bank declined it; everything else
// (e.g. 'pending_verification') is still under review.
export const toBankAccountDisplayStatus = (
  accountStatus: string | undefined,
): BankAccountDisplayStatus => {
  if (accountStatus === 'verified') return 'verified'
  if (accountStatus === 'rejected' || accountStatus === 'verification_failed') return 'rejected'
  return 'pending'
}

export const parseBankAccountList = (resp: unknown): BankAccount[] => {
  const rows = (get(resp, 'data') ?? []) as BankAccountWireDto[]
  return rows.map((account) => ({
    id: account.id || '',
    bankName: account.bankName || '',
    accountNumber: account.accountNumber || '',
    accountStatus: account.accountStatus || '',
    isPrimary: account.isPrimary ?? false,
    rejectionReason: account.rejectionReason ?? null,
  }))
}

export const parseBankAccountListToView = (accounts: BankAccount[]): BankAccountListItemView[] => {
  return accounts.map((account) => ({
    ...account,
    bankName: account.bankName || 'Bank Account',
    maskedNumber: maskAccountNumber(account.accountNumber),
    status: toBankAccountDisplayStatus(account.accountStatus),
  }))
}

export const parseBankAccountDetail = (resp: unknown): BankAccountDetail => {
  const data = (get(resp, 'data') ?? {}) as BankAccountDetailWireDto
  return {
    id: data.id,
    bankName: data.bankName,
    accountHolderName: data.accountHolderName,
    accountNumber: data.accountNumber,
    iban: data.iban ?? null,
    swiftCode: data.swiftCode ?? null,
    currencyCode: data.currencyCode,
    accountStatus: data.accountStatus,
    isPrimary: data.isPrimary,
    createdAt: data.createdAt,
  }
}

export const parseCreateBankAccountResult = (resp: unknown): CreatedBankAccount => ({
  id: get(resp, 'data.id') || '',
  accountStatus: get(resp, 'data.accountStatus') || '',
})
